<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import Box from '@uiKit/elements/Box.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Text from '@uiKit/elements/Text.vue'
import AppButton from '@uiKit/elements/Button.vue'
import MoneyModalHeader from './MoneyModalHeader.vue'

// Figma 34274:239517 — the receipt shown once the withdrawal is accepted:
// arrival note, then what left the wallet, where it went and what's left.
interface Props {
  visible: boolean
  currency: string
  /** Pre-formatted amount, e.g. `"10,000.00"`. */
  amount: string
  /** Bank name plus masked account, e.g. `"Emirates NBD ***3451"`. */
  destination: string
  /** Pre-formatted balance after the withdrawal. */
  remaining: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'view-transactions'): void
}>()

const close = () => emit('update:visible', false)

const onDialogUpdate = (value: boolean) => {
  if (!value) close()
}

const onViewTransactions = () => {
  close()
  emit('view-transactions')
}
</script>

<template>
  <Dialog
    :model-value="visible"
    width="660px"
    :padded="false"
    :show-close-icon="false"
    @update:model-value="onDialogUpdate"
  >
    <Box direction="col" class="wsm">
      <MoneyModalHeader title="Withdrawal Confirmed" @close="close" />

      <Box class="wsm__divider" />

      <Box direction="col" gap="22px" class="wsm__body">
        <Box align="center" justify="center" class="wsm__note">
          <Text
            type="body"
            weight="medium"
            color="primary"
            align="center"
            is="p"
            style="font-style: italic; line-height: 1.38"
          >
            The money will be transferred to your bank account within 48 hours
          </Text>
        </Box>

        <Box direction="col" gap="4" class="wsm__summary">
          <Text type="body" weight="bold" color="muted" transform="uppercase" is="span">
            Withdrawal Summary
          </Text>
          <Box direction="col" gap="4">
            <Box align="center" justify="between" gap="3">
              <Text type="heading" weight="regular" color="muted" is="span">Amount</Text>
              <Text type="heading" weight="bold" align="right" is="span" class="wsm__value">
                <CurrencyText :text="`${currency} ${amount}`" />
              </Text>
            </Box>
            <Box align="center" justify="between" gap="3">
              <Text type="heading" weight="regular" color="muted" is="span">To</Text>
              <Text type="heading" weight="bold" align="right" is="span">{{ destination }}</Text>
            </Box>
            <Box align="center" justify="between" gap="3">
              <Text type="heading" weight="regular" color="muted" is="span">Remaining Balance</Text>
              <Text type="heading" weight="bold" align="right" is="span" class="wsm__value">
                <CurrencyText :text="`${currency} ${remaining}`" />
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box class="wsm__divider" />

      <Box direction="col" gap="3" class="wsm__footer">
        <AppButton label="View Transactions" size="large" block @click="onViewTransactions" />
        <AppButton label="Done" color="secondary" variant="outline" block @click="close" />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.wsm__divider {
  flex-shrink: 0;
  height: 1px;
  background: var(--p-content-border-color);
}

.wsm__body {
  padding: 1.5rem 1.75rem;
}

.wsm__note {
  background: var(--p-primary-50);
  border-radius: 9px;
  padding: 0.5625rem 0.6875rem;
}

.wsm__summary {
  background: var(--p-surface-0);
  border: 1px solid var(--p-surface-300);
  border-radius: 12px;
  padding: 1rem 1.125rem;
}

.wsm__value {
  font-variant-numeric: tabular-nums;
}

.wsm__footer {
  padding: 1.25rem 1.75rem 1.5rem;
}

@media (max-width: 992px) {
  .wsm__body {
    padding: 1.25rem 1rem;
  }

  .wsm__footer {
    padding: 1rem;
  }
}
</style>
